import type { BusinessInfo, Order } from '../types';

export interface SharedReceiptItem {
  name: string;
  quantity: number;
  price: number;
}

export interface SharedReceiptPayload {
  businessName?: string;
  orderId: string;
  createdAt: number;
  customerName?: string;
  /** Optional event token/queue number shown to customer. */
  tokenNumber?: number;
  /** Full token label for shared receipt when numeric queue # is not enough (legacy cohort). */
  tokenDisplay?: string;
  items: SharedReceiptItem[];
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  /**
   * When set, the QR represents a single sub-bill slice (one customer's items + share). The
   * receipt page should render only this slice. Items/totals are already pre-narrowed below.
   */
  subBillId?: string;
  /** Customer/sub-bill name shown on the focused sub-bill receipt (e.g. "John"). */
  subBillName?: string;
  /** When this is a sub-bill receipt, the selected discount allocation mode for transparency. */
  subBillDiscountAllocationMode?: 'proportional' | 'remainder';
}

const toBase64 = (value: string): string => {
  try {
    return btoa(unescape(encodeURIComponent(value)));
  } catch {
    return '';
  }
};

const fromBase64 = (value: string): string => {
  try {
    return decodeURIComponent(escape(atob(value)));
  } catch {
    return '';
  }
};

export type SharedReceiptTokenOpts = {
  tokenNumber?: number;
  tokenDisplay?: string;
  /** When set, encode only this sub-bill's slice (items + share + totals) into the payload. */
  subBillId?: string;
};

export const createSharedReceiptPayload = (
  order: Order,
  businessInfo?: BusinessInfo,
  tokenOpts?: number | SharedReceiptTokenOpts
): SharedReceiptPayload => {
  const tokenNumber =
    typeof tokenOpts === 'number'
      ? tokenOpts
      : typeof tokenOpts === 'object' && tokenOpts
        ? tokenOpts.tokenNumber
        : undefined;
  const tokenDisplayRaw =
    typeof tokenOpts === 'object' && tokenOpts ? tokenOpts.tokenDisplay?.trim() : undefined;
  const subBillId = typeof tokenOpts === 'object' && tokenOpts ? tokenOpts.subBillId : undefined;

  // Build items and totals — narrowed to a single sub-bill if subBillId is provided.
  let items: SharedReceiptItem[];
  let subtotal: number;
  let taxAmount: number;
  let totalAmount: number;
  let subBillName: string | undefined;
  let subBillDiscountAllocationMode: 'proportional' | 'remainder' | undefined;

  if (subBillId && order.subBills?.length) {
    const sb = order.subBills.find((s) => s.id === subBillId);
    if (sb) {
      // Build a name lookup so the customer-facing payload can show readable item names.
      const itemNameByLineId = new Map<string, { name: string }>();
      (order.items ?? []).forEach((it, idx) => {
        const lineId = it.lineId ?? `idx-${idx}`;
        itemNameByLineId.set(lineId, { name: it.name });
      });
      items = sb.itemAllocations.map((alloc) => {
        const meta = itemNameByLineId.get(alloc.lineId);
        const unitPrice = alloc.quantity > 0 ? alloc.lineSubtotal / alloc.quantity : 0;
        return {
          name: meta?.name ?? 'Item',
          quantity: alloc.quantity,
          price: Math.round(unitPrice * 100) / 100,
        };
      });
      totalAmount = Math.round(sb.total * 100) / 100;
      taxAmount = Math.round(totalAmount * (15 / 115) * 100) / 100;
      subtotal = Math.round((totalAmount - taxAmount) * 100) / 100;
      subBillName = sb.name || undefined;
      subBillDiscountAllocationMode = sb.discountAllocationMode;
    } else {
      // Sub-bill id provided but not found — fall back to whole-order receipt.
      items = (order.items ?? []).map((item) => ({
        name: item.name,
        quantity: Number(item.quantity) || 0,
        price: Number(item.price) || 0,
      }));
      subtotal = Number(order.subtotal ?? order.totalAmount ?? 0);
      taxAmount = Number(order.taxAmount ?? subtotal * (15 / 115));
      totalAmount = Number(order.totalAmount ?? subtotal);
    }
  } else {
    items = (order.items ?? []).map((item) => ({
      name: item.name,
      quantity: Number(item.quantity) || 0,
      price: Number(item.price) || 0,
    }));
    subtotal = Number(order.subtotal ?? order.totalAmount ?? 0);
    taxAmount = Number(order.taxAmount ?? subtotal * (15 / 115));
    totalAmount = Number(order.totalAmount ?? subtotal);
  }

  return {
    businessName: businessInfo?.businessName || undefined,
    orderId: order.id,
    createdAt: Number(order.timestamp) || Date.now(),
    customerName: order.customerName || undefined,
    tokenNumber: typeof tokenNumber === 'number' && tokenNumber > 0 ? tokenNumber : undefined,
    tokenDisplay: tokenDisplayRaw || undefined,
    items,
    subtotal,
    taxAmount,
    totalAmount,
    subBillId: subBillId || undefined,
    subBillName,
    subBillDiscountAllocationMode,
  };
};

export const encodeSharedReceiptPayload = (payload: SharedReceiptPayload): string => {
  return toBase64(JSON.stringify(payload));
};

export const decodeSharedReceiptPayload = (encoded: string): SharedReceiptPayload | null => {
  const raw = fromBase64(encoded);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as SharedReceiptPayload;
    if (!parsed || !parsed.orderId || !Array.isArray(parsed.items)) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const buildSharedReceiptUrl = (payload: SharedReceiptPayload): string => {
  const encoded = encodeSharedReceiptPayload(payload);
  const url = new URL(window.location.href);
  url.search = '';
  url.hash = '';
  url.searchParams.set('view', 'receipt');
  url.searchParams.set('data', encoded);
  return url.toString();
};
